/**
 * Analytics, via Microsoft Clarity.
 *
 * GA4 was removed in favour of this: its property never accepted a single hit
 * from setup onwards, while shipping ~160KB (502KB parsed) of gtag.js to every
 * visitor to send nothing. Clarity is ~74KB and demonstrably records data.
 *
 * Two primitives, because Clarity surfaces them in different places:
 *   - `trackEvent` -> Clarity "events", visible in the Dashboard, Filters and
 *     Recordings verticals. Use for things that happen.
 *   - `setTag` -> Clarity "custom tags", visible in Filters. These are
 *     SESSION-scoped, not event-scoped: a tag set at any point labels the whole
 *     session. Use for what a session *is*, not for what happened in it.
 *
 * Both are safe to call before Clarity's script has downloaded — its snippet
 * installs a synchronous queueing stub in <head>, so early calls are buffered.
 * The script itself is deferred to idle, so that matters.
 */

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

/** Record that something happened. Names should be stable and lower_snake_case. */
export function trackEvent(name: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.clarity?.('event', name);
  } catch {
    // Analytics must never break the page.
  }
}

/** Label the session. Session-scoped — see the note above. */
export function setTag(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.clarity?.('set', key, value);
  } catch {
    // Analytics must never break the page.
  }
}

/**
 * Convenience for the funnel call sites: fires the event and labels the session
 * with any params worth filtering on.
 *
 * Params become session tags rather than event properties, because Clarity has
 * no event-property concept. That is the right shape for the things we pass
 * (which form variant someone used, whether they supplied a birth place) since
 * those are properties of the visit, not of the individual moment.
 */
export function track(name: string, params?: Record<string, unknown>): void {
  trackEvent(name);
  if (!params) return;
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    setTag(key, String(value));
  }
}
